<template>
  <o-dialog
    ref="dialog"
    :title="$t('discount.edit')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.apply'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
  >
    <div v-if="reservation" class="pt-4 text-left space-y-4">
      <div>
        <div>
          <p class="text-gray-800 text-sm" leading-none>
            {{ reservation.service.title }}
          </p>
          <p class="text-gray-600 text-xs leading-none mb-1">
            {{ reservation.employee.displayName }}
          </p>
        </div>

        <discount-select
          :service="reservation.service"
          :employee="reservation.employee"
          v-model:customerPass="customerPass"
          :customerPasses="options.availableCustomerPasses"
          v-model:voucher="voucher"
          :vouchers="options.availableVouchers"
          :placeholder="$t('pass.select')"
          class="w-full"
        />
      </div>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import DiscountSelect from '@/components/reservation/elements/DiscountSelect.vue'

interface Props {
  reservation?: Record<string, any> | null
  portal?: string | null
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
  portal: null,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:reservation': [value: any]
}>()

const { $api, $notification, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const customerPass = ref<any>(null)
const voucher = ref<any>(null)
const originalCustomerPass = ref<any>(null)
const originalVoucher = ref<any>(null)

const info = computed(() => {
  return $i18n.t('components.reservationCustomer.actions.discount.info', { name: props.reservation?.customer?.displayName, label: props.reservation?.reservation.infoLabel })
})

const options = computed(() => {
  return props.reservation?.options
})

const changedPass = computed(() => {
  return customerPass.value !== originalCustomerPass.value
})

const changedVoucher = computed(() => {
  return voucher.value !== originalVoucher.value
})

function open() {
  dialog.value.open()

  customerPass.value = props.reservation!.customerPass
  voucher.value = props.reservation!.voucher

  originalCustomerPass.value = props.reservation!.customerPass
  originalVoucher.value = props.reservation!.voucher
}

async function confirm() {
  emit('update:loading', true)

  const customerPassVal = customerPass.value
  const voucherVal = voucher.value

  try {
    let reservation
    const reservationCustomerId = props.reservation?.idReservationCustomer?.toString()

    if (changedPass.value) {
      if (customerPassVal) {
        reservation = (await $api.reservationCustomer.assignCustomerPass(reservationCustomerId, customerPassVal.idCustomerPass?.toString())).data
      } else {
        reservation = (await $api.reservationCustomer.removeCustomerPass(reservationCustomerId)).data
      }
    }

    if (changedVoucher.value) {
      if (voucherVal) {
        reservation = (await $api.reservationCustomer.applyVoucher(reservationCustomerId, voucherVal.idVoucher?.toString())).data
      } else {
        reservation = (await $api.reservationCustomer.removeVoucher(reservationCustomerId)).data
      }
    }

    if (reservation) {
      emit('update:reservation', reservation)
      emit('update:loading', false)

      $notification.toast({
        title: $i18n.t('discount.success'),
        text: $i18n.t('discount.successInfo', { name: props.reservation!.customer.displayName, label: props.reservation!.reservation.infoLabel }),
        variant: 'success',
      })

      return true
    }
  } catch (e: any) {
    console.log(e)

    $notification.error({ error: e?.errors })
  }
}

defineExpose({ open })
</script>
